import { gql } from 'graphql-request';

const buildCommentSelection = (maxDepth: number, depth = 0): string => {
  const base = `
    id
    body
    bodyLocale
    rating
    createdAt
    updatedAt
    commentableId
    commentableType
    parentCommentId
    recordingId
    depth
    hasChildren
    childCount
    ranking
    likeCount
    user {
      id
      firebaseUserId
      displayName
      avatar {
        lgUrl
        smUrl
      }
    }
    connectedRecording {
      id
      title
      obfuscatedSlug
      obfuscatedId
      distanceMeters
      elapsedSeconds
      user {
        id
        displayName
      }
    }
    images {
      id
      file {
        id
        lgUrl
        smUrl
        mdUrl
      }
    }
    likes {
      id
      user {
        firebaseUserId
      }
    }
  `;

  if (depth >= maxDepth) {
    return base;
  }

  return `
    ${base}
    childComments: paginatedChildComments {
      ${buildCommentSelection(maxDepth, depth + 1)}
    }
  `;
};

const COMMENT_SELECTION = buildCommentSelection(4);
const COMMENT_SELECTION_MUTATION = buildCommentSelection(0);

export const GenerateDirectUpload = gql`
  mutation GenerateDirectUpload($input: GenerateDirectUploadInput!) {
    generateDirectUpload(input: $input) {
      uploadUrl
      headers
      signedId
    }
  }
`;

export const GetHikeComments = gql`
  query GetHikeComments($hikeId: ID!, $sort: String, $page: Int = 1, $perPage: Int = 10) {
    hike(id: $hikeId) {
      id
      name
      averageRating
      commentsCount
      ratingsCount
    }
    commentsConnection: comments(commentableType: "Hike", commentableId: $hikeId, sort: $sort, page: $page, perPage: $perPage) {
      comments {
        ${COMMENT_SELECTION}
      }
      totalCount
      totalPages
      currentPage
      perPage
      hasNextPage
      hasPreviousPage
    }
  }
`;

export const GetRecordingComments = gql`
  query GetRecordingComments($recordingId: ID!, $sort: String, $page: Int = 1, $perPage: Int = 10) {
    recording(id: $recordingId) {
      id
      title
      averageRating
      commentsCount
      ratingsCount
    }
    commentsConnection: comments(commentableType: "Recording", commentableId: $recordingId, sort: $sort, page: $page, perPage: $perPage) {
      comments {
        ${COMMENT_SELECTION}
      }
      totalCount
      totalPages
      currentPage
      perPage
      hasNextPage
      hasPreviousPage
    }
  }
`;

export const GetHikeListComments = gql`
  query GetHikeListComments($hikeListId: ID!, $sort: String, $page: Int = 1, $perPage: Int = 10) {
    hikeList(id: $hikeListId) {
      id
      name
      averageRating
      commentsCount
    }
    commentsConnection: comments(commentableType: "HikeList", commentableId: $hikeListId, sort: $sort, page: $page, perPage: $perPage) {
      comments {
        ${COMMENT_SELECTION}
      }
      totalCount
      totalPages
      currentPage
      perPage
      hasNextPage
      hasPreviousPage
    }
  }
`;

export const GetCommentsPage = gql`
  query GetCommentsPage($commentableType: String!, $commentableId: ID!, $sort: String, $page: Int!, $perPage: Int = 10) {
    commentsConnection: comments(commentableType: $commentableType, commentableId: $commentableId, sort: $sort, page: $page, perPage: $perPage) {
      comments {
        ${COMMENT_SELECTION}
      }
      totalCount
      totalPages
      currentPage
      perPage
      hasNextPage
      hasPreviousPage
    }
  }
`;

export const CreateComment = gql`
  mutation CreateComment(
    $commentableId: ID!
    $commentableType: String!
    $body: String
    $bodyLocale: String
    $rating: Int
    $parentCommentId: ID
    $imageSignedIds: [String!]
  ) {
    createComment(
      input: {
        commentableId: $commentableId
        commentableType: $commentableType
        body: $body
        bodyLocale: $bodyLocale
        rating: $rating
        parentCommentId: $parentCommentId
        imageSignedIds: $imageSignedIds
      }
    ) {
      ${COMMENT_SELECTION_MUTATION}
    }
  }
`;

export const UpdateComment = gql`
  mutation UpdateComment(
    $id: ID!
    $body: String
    $bodyLocale: String
    $rating: Int
    $imageSignedIds: [String!]
  ) {
    updateComment(
      input: {
        id: $id
        body: $body
        bodyLocale: $bodyLocale
        rating: $rating
        imageSignedIds: $imageSignedIds
      }
    ) {
      comment {
        ${COMMENT_SELECTION_MUTATION}
      }
      errors
    }
  }
`;

export const DestroyComment = gql`
  mutation DestroyComment($id: ID!) {
    destroyComment(
      input: {
        id: $id
      }
    ) {
      success
      errors
    }
  }
`;
